// Per-primitive knowledge registry — the composition equivalent of
// interactionRegistry.ts. This is the single source the composing agent (Analyst
// + Designer) reads to plan and author a composed experience, AND the source the
// validator derives its per-primitive checks from. One contract, two readers —
// so the agent never drifts from what the runtime + validator actually enforce.
//
// ADDITIVE: this does NOT replace interactionRegistry.ts (the fixed interaction
// types / Library). It describes the Wave 1 primitive BLOCKS the Workshop
// composes from.
//
// Pure module — no React, no network. Safe to import anywhere.

import { PRIMITIVE_TYPES, type CaptureClass, type PrimitiveType } from './types.js';

export interface PrimitiveSpec {
  id: PrimitiveType;
  name: string;

  // --- Selection knowledge (feeds the composing agent) ----------------------
  /** The learner's action in one plain phrase. */
  action: string;
  /** When to reach for this block. */
  whenToUse: string;
  /** THE MECHANISM — why this block captures something worth capturing. */
  whyItWorks: string;
  /** Anti-patterns — do NOT use this block if any apply. */
  notFor: string[];

  // --- Capture contract -----------------------------------------------------
  captureClass: CaptureClass;
  /** Whether the block can produce a normalised [0,1] score (gradeable). */
  scoreable: boolean;
  /** Whether the block may be the target of another block's `ref`. */
  refTarget: boolean;
  /** Whether this block MUST carry a `ref` to an upstream block. */
  requiresRef: boolean;

  // --- Content sufficiency (feeds the content gate + Builder + QA) ----------
  /** What the authored content must contain for the block to land. */
  minimumViableContent: string;
  /** Distilled "reject if" rules — the validator derives checks from these. */
  qaRejectRules: string[];

  /** Close tokens this block contributes, block-scoped: `{<id>.<token>}`. */
  closeTokens: string[];
}

export const PRIMITIVE_REGISTRY: Record<PrimitiveType, PrimitiveSpec> = {
  stimulus: {
    id: 'stimulus',
    name: 'Stimulus',
    action: 'Reads a framed situation (no input).',
    whenToUse:
      'To set the scene, pose the tension, or supply the material a later block acts on. Every experience that asks the learner to judge something needs the something framed first.',
    whyItWorks:
      'A concrete, specific situation gives the subsequent actionable blocks something real to bite on — abstraction kills the judgement.',
    notFor: ['Padding — a stimulus with no downstream block that uses it is dead weight.'],
    captureClass: 'none',
    scoreable: false,
    refTarget: false,
    requiresRef: false,
    minimumViableContent: 'A specific, concrete situation or piece of material (not a generic preamble).',
    qaRejectRules: ['Body is empty', 'Body is generic scene-setting with no specifics'],
    closeTokens: [],
  },

  choice: {
    id: 'choice',
    name: 'Choice',
    action: 'Picks one option and gets feedback.',
    whenToUse:
      'A single decision with a defensible best answer and plausible wrong ones — the most basic actionable move. Also the way to capture a PREDICTION a later `reveal` pays off.',
    whyItWorks:
      'Committing to one option before any reveal forces a position; the per-option feedback then teaches against exactly the reasoning the learner used.',
    notFor: [
      'There is no defensible best answer (use rate-slider for a weighting, or commit-box for an opinion)',
      'The options are not genuinely distinct',
    ],
    captureClass: 'actionable',
    scoreable: true,
    refTarget: true,
    requiresRef: false,
    minimumViableContent:
      'A prompt plus 2–4 distinct options; when scored, exactly one marked correct and the distractors plausible; per-option feedback that teaches.',
    qaRejectRules: [
      'Fewer than 2 options',
      'More than one option marked correct',
      'No option marked correct while the block is referenced by scoring',
      'A distractor is obviously absurd (no evaluation to do)',
    ],
    closeTokens: ['choice', 'correct'],
  },

  'rate-slider': {
    id: 'rate-slider',
    name: 'Rate Slider',
    action: 'Places a weighting on a continuum.',
    whenToUse:
      'A position on a spectrum where the point is the weighting itself (speed vs safety, confidence, how strongly you agree). Pair a second rate-slider via `ref` to capture a shift.',
    whyItWorks:
      'A continuous position captures nuance a multiple-choice cannot; re-asking it after a reveal makes a change of mind concrete and ownable — the delta is the payload.',
    notFor: [
      'A right/wrong fact (use choice)',
      'A spectrum with no meaningful ends to anchor it',
    ],
    captureClass: 'actionable',
    scoreable: false,
    refTarget: true,
    requiresRef: false,
    minimumViableContent:
      'A prompt, min/max bounds, and labelled ends. Scored only when a `target` value + tolerance is authored.',
    qaRejectRules: ['min ≥ max', 'Missing end labels leave the scale meaningless', 'target outside [min,max] when scored'],
    closeTokens: ['value', 'before', 'after', 'delta'],
  },

  'sort-zones': {
    id: 'sort-zones',
    name: 'Sort into Zones',
    action: 'Drags items into labelled zones.',
    whenToUse:
      'Categorisation where placing each item requires a judgement — matching, grouping, prioritising into named buckets.',
    whyItWorks:
      'Committing every item to a zone forces a decision on each; the correct/total score reads back exactly how well the category boundaries are understood.',
    notFor: [
      'Items map to zones trivially (no judgement)',
      'A single ordering (use a ranking type from the Library)',
    ],
    captureClass: 'actionable',
    scoreable: true,
    refTarget: true,
    requiresRef: false,
    minimumViableContent: '2–4 labelled zones and 4–8 items, each with a defensible correct zone.',
    qaRejectRules: [
      'Fewer than 2 zones',
      'An item’s correctZone references no zone id',
      'Every item belongs to the same zone (no sorting to do)',
    ],
    closeTokens: ['correct', 'total'],
  },

  'select-evidence': {
    id: 'select-evidence',
    name: 'Select Evidence',
    action: 'Picks the strong pieces from a pool with distractors.',
    whenToUse:
      'Assembling support for a claim from a pool that mixes strong, weak, and irrelevant evidence — evaluating each piece is the work.',
    whyItWorks:
      'Choosing from a pool salted with plausible-but-weak distractors forces evaluation of every piece against the claim; the overlap with the expert set grades the judgement.',
    notFor: [
      'All evidence is unambiguously relevant (no evaluation)',
      'There is no claim to assemble evidence for',
    ],
    captureClass: 'actionable',
    scoreable: true,
    refTarget: true,
    requiresRef: false,
    minimumViableContent:
      'A claim, an evidence pool with at least 2 plausible distractors, and at least 1 piece flagged `strong` (the expert set).',
    qaRejectRules: [
      'Fewer than 2 distractors (non-strong items)',
      'No item flagged strong',
      'maxSelections below the strong-item count',
    ],
    closeTokens: ['correct', 'total', 'picked'],
  },

  reveal: {
    id: 'reveal',
    name: 'Reveal',
    action: 'Uncovers the expert view after the learner has committed.',
    whenToUse:
      'Immediately AFTER an actionable block, to pay off the commitment — model the expert reasoning and surface the gap between it and the learner’s own move.',
    whyItWorks:
      'A reveal only teaches once the learner owns a position; showing the expert model against their captured answer turns a passive explanation into a self-relevant contrast.',
    notFor: [
      'Before the learner has committed (commit-before-reveal is the core invariant)',
      'Standalone exposition (that is a stimulus)',
    ],
    captureClass: 'none',
    scoreable: false,
    refTarget: false,
    requiresRef: true,
    minimumViableContent: 'An expert view / model answer that genuinely contrasts with a likely learner answer.',
    qaRejectRules: [
      'No ref, or ref points at a non-actionable block',
      'Placed before the block it reveals against',
      'Body just restates the correct answer with no reasoning',
    ],
    closeTokens: [],
  },

  confidence: {
    id: 'confidence',
    name: 'Confidence',
    action: 'Self-reports how sure they are on a scale.',
    whenToUse:
      'To capture certainty — most powerfully as a PAIR: rate confidence, meet a reveal, then re-rate. The change is the signal (a measured shift of mind).',
    whyItWorks:
      'Naming a confidence level makes it inspectable; re-rating after evidence turns "I changed my mind" into a concrete, ownable delta — the heart of conceptual change.',
    notFor: [
      'A right/wrong judgement (use choice)',
      'A one-off with no reveal or re-rate to pay it off',
    ],
    captureClass: 'actionable',
    scoreable: false,
    refTarget: true,
    requiresRef: false,
    minimumViableContent: 'A prompt and labelled ends (e.g. "Guessing" → "Certain"). Never graded — it is a self-report.',
    qaRejectRules: ['min ≥ max', 'Used once with nothing to pay off the rating'],
    closeTokens: ['value', 'before', 'after', 'delta'],
  },

  'flag-in-text': {
    id: 'flag-in-text',
    name: 'Flag in Text',
    action: 'Clicks the wrong or weak spans in a passage.',
    whenToUse:
      'When the skill is catching errors, unsupported claims, or weak moves inside real prose — a draft to red-pen, an argument to audit, code/copy to review.',
    whyItWorks:
      'Finding the flaw in situ (not from a list) is the actual transfer task; salting honest text among the flawed spans forces genuine reading, and over-flagging is penalised.',
    notFor: [
      'There is no passage — just discrete claims (use select-evidence)',
      'Every span is obviously wrong (no reading to do)',
    ],
    captureClass: 'actionable',
    scoreable: true,
    refTarget: true,
    requiresRef: false,
    minimumViableContent:
      'A passage split into segments, with the wrong/weak ones flagged and enough honest segments that catching the flaws takes real reading (aim for at least as many honest segments as flagged, and 2+ decoys). Segment the whole passage at a consistent granularity — do not cut the flagged spans into short, precise phrases while leaving the honest text as long blocks, or the answers stand out by shape even though every span is clickable.',
    qaRejectRules: ['No segment flagged', 'Every segment flagged (nothing honest to distinguish)', 'Too few honest decoys, or flagged spans cut much shorter than the honest text (answers guessable by shape)'],
    closeTokens: ['correct', 'total', 'picked'],
  },

  'perspective-lens': {
    id: 'perspective-lens',
    name: 'Perspective Lens',
    action: 'Writes a short take from each of several named lenses.',
    whenToUse:
      'To force multi-angle thinking — the same situation seen through distinct stakeholders, disciplines, or timeframes. Articulation, so it is kept and shown back, not graded.',
    whyItWorks:
      'Committing a take per lens surfaces angles the learner would otherwise skip; the set of takes is a record of their reasoning across perspectives.',
    notFor: [
      'There is a defensible best answer (use choice / select-evidence)',
      'Only one lens genuinely applies',
    ],
    captureClass: 'articulation',
    scoreable: false,
    refTarget: false,
    requiresRef: false,
    minimumViableContent: '2–4 genuinely distinct lenses, each with a clear label the learner can inhabit.',
    qaRejectRules: ['Fewer than 2 lenses', 'Lenses are near-duplicates'],
    closeTokens: [],
  },

  'commit-box': {
    id: 'commit-box',
    name: 'Commit Box',
    action: 'States a specific, constrained commitment in their own words.',
    whenToUse:
      'To close on a personal, concrete commitment — what the learner will now DO or believe — retained and shown back to them.',
    whyItWorks:
      'Articulating a specific commitment in one’s own words consolidates the learning and creates ownership; it is kept and echoed, never graded, so honesty is safe.',
    notFor: [
      'Anything the platform is expected to grade (articulation is never auto-scored)',
      'A vague "reflect on this" with no constraint (demand a specific commitment)',
    ],
    captureClass: 'articulation',
    scoreable: false,
    refTarget: true,
    requiresRef: false,
    minimumViableContent: 'A prompt that demands a specific, constrained commitment (not open-ended musing).',
    qaRejectRules: ['Prompt is generic ("what did you learn?")', 'minWords/maxWords contradictory'],
    closeTokens: ['text'],
  },
};

/** All close tokens a block exposes, fully block-scoped (`<id>.<token>`). */
export function blockCloseTokens(blockId: string, type: PrimitiveType): string[] {
  return PRIMITIVE_REGISTRY[type].closeTokens.map((t) => `${blockId}.${t}`);
}

/**
 * Render the primitive reference for the Composer prompt — the same
 * one-contract-two-readers pattern as renderInteractionReferenceForPrompt: the
 * composing agent reads this to pick + author blocks, and it mirrors exactly
 * what the validator + runtime enforce. Serialization stays lean (no reviewer
 * fields): action, when/why, anti-patterns, capture contract, content gate,
 * ref rules, and the block-scoped close tokens each primitive exposes.
 */
export function renderPrimitiveReferenceForPrompt(): string {
  const blocks = PRIMITIVE_TYPES.map((id) => {
    const p = PRIMITIVE_REGISTRY[id];
    const lines = [
      `**${p.name} (\`${p.id}\`)** · capture: ${p.captureClass}${p.scoreable ? ' (scoreable)' : ''}`,
      `- Action: ${p.action}`,
      `- When to use: ${p.whenToUse}`,
      `- Why it works: ${p.whyItWorks}`,
      `- Avoid: ${p.notFor.join('; ')}`,
      `- Needs in content: ${p.minimumViableContent}`,
    ];
    if (p.requiresRef) lines.push('- MUST carry a `ref` to an earlier actionable block.');
    else if (p.refTarget) lines.push('- May be the target of another block’s `ref`.');
    if (p.closeTokens.length) {
      lines.push(`- Close tokens (block-scoped): ${p.closeTokens.map((t) => `{<id>.${t}}`).join(', ')}`);
    }
    return lines.join('\n');
  });

  return [
    '## Primitive block reference',
    '',
    'Compose the experience from these blocks, top-to-bottom, in order. Grammar the composition MUST satisfy:',
    '- At least one **actionable** block (choice / rate-slider / sort-zones / select-evidence).',
    '- A `reveal` must FOLLOW and `ref` an actionable block it reveals against — never precede it (commit-before-reveal).',
    '- Every `ref` points at an EARLIER block (no orphan/forward/self refs).',
    '- `scoring` lists which actionable, scoreable blocks count and how they roll up.',
    '- The close reflects the learner’s own values back via `{block-id.token}` slots; author it as staged beats (each ≤ ~60 words), never a wall of text.',
    '- Separate `structure` (settings) from `content` (all authored text) in every block.',
    '',
    blocks.join('\n\n'),
  ].join('\n');
}
